import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AdminOverrideProvider } from './hooks/useAdminOverride';
import { initSentry, Sentry } from './lib/sentry';

// Initialise error/performance/replay monitoring before the app mounts.
initSentry();

// API Request Counter (for debugging - remove in production)
if (import.meta.env.DEV) {
  const requestCounts: Record<string, number> = {};
  const originalFetch = window.fetch;

  window.fetch = async (...args) => {
    const url = typeof args[0] === 'string' ? args[0] : args[0] instanceof Request ? args[0].url : 'unknown';
    const endpoint = url.split('?')[0]; // Remove query params for grouping

    requestCounts[endpoint] = (requestCounts[endpoint] || 0) + 1;

    console.log(`[API] ${endpoint} (count: ${requestCounts[endpoint]})`);

    return originalFetch(...args);
  };

  // Log summary every 10 seconds
  setInterval(() => {
    const total = Object.values(requestCounts).reduce((a, b) => a + b, 0);
    if (total > 0) {
      console.log('[API Summary]', { total, byEndpoint: { ...requestCounts } });
    }
  }, 10000);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 60 * 1000, // 30 minutes - data stays fresh
      gcTime: 60 * 60 * 1000, // 1 hour - keep in cache even when unused
      refetchOnWindowFocus: false, // Don't refetch when switching tabs
      refetchOnMount: false, // Don't refetch if data exists
      refetchOnReconnect: false, // Don't refetch on network reconnect
      retry: 1, // Only retry once on failure
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={
        <div className="h-screen flex flex-col items-center justify-center gap-3 text-gray-500">
          <p className="text-lg">Something went wrong.</p>
          <button
            className="text-sm text-blue-500 dark:text-maven-cyan-300 hover:underline"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      }
    >
      <QueryClientProvider client={queryClient}>
        <AdminOverrideProvider>
          <App />
        </AdminOverrideProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
